import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import { Action, NgxsOnInit, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { DossierStateModel, DossierStateToken } from '@stores/dossier/dossier.state';
import { JwtDelete } from '@stores/jwt/jwt.action';
import { JwtState } from '@stores/jwt/jwt.state';
import { GetUserInfo, Init, Logout, SetUser, UpdateIdentityCard } from '@stores/session/session.action';
import { WINDOW } from '@wizbii/angular-utilities';
import { IdentityCard, UserOverview } from '@wizbii/models';
import { AccountWebservice, AuthenticationWebservice } from '@wizbii/webservices';
import { combineLatest } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

export class SessionStateModel {
  user?: UserOverview;
  identityCard?: IdentityCard;
}

const SessionStateToken = new StateToken<SessionStateModel>('session');

@State({
  name: SessionStateToken,
  defaults: null,
})
@Injectable()
export class SessionState implements NgxsOnInit {
  @Selector([SessionStateToken])
  static isInitialized(state: SessionStateModel): boolean {
    return !!state;
  }

  @Selector([SessionStateToken])
  static identityCard(state: SessionStateModel): IdentityCard | undefined {
    return state?.identityCard;
  }

  @Selector([SessionStateToken])
  static user(state: SessionStateModel): UserOverview | undefined {
    return state?.user;
  }

  @Selector([SessionStateToken])
  static isB2BUser(state: SessionStateModel): boolean {
    return !!state?.user?.tags && !!state.user.tags.find((tag) => tag === 'money-free');
  }

  @Selector([DossierStateToken])
  static isClient(state: DossierStateModel): boolean {
    return state?.length > 0;
  }

  private readonly accountLogout = `${environment.urls.account.logout}${this.window.location.origin}`;

  constructor(
    @Inject(WINDOW) private readonly window: any,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly authWebservice: AuthenticationWebservice,
    private readonly store: Store,
    private readonly accountWebservice: AccountWebservice
  ) {}

  ngxsOnInit(ctx: StateContext<SessionStateModel>) {
    this.store.dispatch(new Init());
  }

  @Action(Init)
  init(ctx: StateContext<SessionStateModel>) {
    return this.store.select(JwtState.userId).pipe(
      tap((userId) => !userId && ctx.setState({ identityCard: undefined, user: undefined })),
      filter((userId) => !!userId),
      switchMap((userId) => ctx.dispatch(new GetUserInfo(userId)))
    );
  }

  @Action(GetUserInfo)
  getUserInfo(ctx: StateContext<SessionStateModel>, { userId }: GetUserInfo) {
    return combineLatest([
      this.authWebservice.getUserOverview(userId),
      this.accountWebservice.getIdentityCard(userId),
    ]).pipe(
      tap(([userOverview, identityCard]) =>
        ctx.patchState({
          user: userOverview,
          identityCard,
        })
      )
    );
  }

  @Action(UpdateIdentityCard)
  updateIdentityCard(ctx: StateContext<SessionStateModel>, { identityCard }: UpdateIdentityCard) {
    const updatedIdentityCard = { ...ctx.getState().identityCard, ...identityCard };
    return this.accountWebservice
      .updateIdentityCard(updatedIdentityCard as IdentityCard)
      .pipe(tap(() => ctx.patchState({ identityCard: updatedIdentityCard as IdentityCard })));
  }

  @Action(SetUser)
  setUser(ctx: StateContext<SessionStateModel>, { user }: SetUser) {
    ctx.patchState({ user });
  }

  @Action(Logout)
  logout(ctx: StateContext<SessionStateModel>) {
    return ctx.dispatch(new JwtDelete()).pipe(tap(() => this.window.open(this.accountLogout, '_self')));
  }
}
